import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import DashboardLayout from '../components/DashboardLayout';
import Input from '../components/elements/Input';
import { getReceiptById } from '../services/receipts';
import toast from 'react-hot-toast';
import { PDFViewer } from '@react-pdf/renderer';
import ReceiptPdf from '../components/pdf/ReceiptPdf';
import ReceiptPdfButton from '../components/pdf/ReceiptPdfButton';
import { getFileOpeningById } from '../services/fileOpenings';
import { renderAddress } from '../utils/helper';

const ReceiptView = () => {
    const { id, receiptId } = useParams();
    const navigate = useNavigate();

    const [receiptData, setReceiptData] = useState({
        date: '',
        client_name: '',
        address: '',
        phone: '',
        mobile: '',
        amount: '0',
        balance_amount: '0',
        previous_balance: '0',
        payment_method: 'cash',
        payment_received: false,
        reference_number: '',
        comments: ''
    });
    const [loading, setLoading] = useState(true);
    const [showPdfPreview, setShowPdfPreview] = useState(false);

    // Fetch receipt data
    useEffect(() => {
        const fetchReceiptData = async () => {
            try {
                setLoading(true);

                // Always fetch file opening data first
                const fileData = await getFileOpeningById(id);

                const receipt = await getReceiptById(receiptId);

                const formattedData = {
                    ...receipt,
                    client_name: fileData.title + ' ' + fileData.forename + ' ' + fileData.surname,
                    address: `${renderAddress(fileData)}`,
                    phone: fileData.phone || '',
                    mobile: fileData.mobile,
                    date: receipt.date ? receipt.date.split('T')[0] : '',
                    amount: receipt.amount?.toString() || '0',
                    balance_amount: receipt.balance_amount?.toString() || '0',
                    previous_balance: receipt.previous_balance?.toString() || '0',
                    comments: receipt.comments || ''
                };

                setReceiptData(formattedData);

            } catch (error) {
                toast.error(error.message);
            } finally {
                setLoading(false);
            }
        };

        if (receiptId) {
            fetchReceiptData();
        }
    }, [id, receiptId]);

    const getPaymentMethodLabel = (method) => {
        const methodLabels = {
            'cash': 'Cash',
            'card': 'Card',
            'bank': 'Bank Transfer',
            'other': 'Other'
        };
        return methodLabels[method] || method;
    };

    const getPaymentStatusBadge = (paymentReceived) => {
        return paymentReceived ? (
            <span className="px-3 py-1 rounded-full text-sm bg-green-100 text-green-800">
                Received
            </span>
        ) : (
            <span className="px-3 py-1 rounded-full text-sm bg-yellow-100 text-yellow-800">
                Pending
            </span>
        );
    };

    if (loading) {
        return <DashboardLayout pageTitle="Loading..."><p className='dark:text-white'>Loading...</p></DashboardLayout>;
    }

    return (
        <DashboardLayout pageTitle="View Receipt">
            <div className="max-w-4xl mx-auto">
                <div className='flex justify-between mb-4'>
                    <div>
                        <button onClick={() => navigate(-1)} className="bg-blue-600 hover:bg-blue-700 text-white font-semibold px-4 py-2 rounded-md shadow ml-4">
                            Back
                        </button>
                    </div>
                    <div>
                        {showPdfPreview && receiptData && (
                            <div className="text-right">
                                <button
                                    type="button"
                                    onClick={() => setShowPdfPreview(!showPdfPreview)}
                                    className="mr-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                                >
                                    Hide Preview
                                </button>
                            </div>
                        )}
                    </div>
                </div>
                {showPdfPreview && receiptData && (
                    <div style={{ height: '80vh' }} className="mb-4 text-right">
                        <PDFViewer width="100%" height="100%">
                            <ReceiptPdf data={receiptData} />
                        </PDFViewer>
                    </div>
                )}
                <div className={showPdfPreview ? "h-10" : ""}></div>
                <div className="bg-white dark:bg-gray-800 shadow-md rounded-lg p-4 sm:p-8 mb-10">
                    <div className="flex justify-between items-center mb-6">
                        <h1 className="text-xl sm:text-2xl font-bold text-center text-gray-800 dark:text-white">
                            View Receipt
                        </h1>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        {/* Left Column */}
                        <div className="space-y-4">
                            <Input
                                label="Date"
                                type="date"
                                name="date"
                                value={receiptData.date}
                                readOnly
                                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                            />

                            <Input
                                label="Payment Amount (£)"
                                type="number"
                                name="amount"
                                value={receiptData.amount}
                                readOnly
                                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed font-semibold"
                            />

                            <div>
                                <label className="block font-semibold mb-1 text-gray-800 dark:text-white">
                                    Payment Method
                                </label>
                                <div className="w-full border rounded-md p-2 bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 cursor-not-allowed">
                                    {getPaymentMethodLabel(receiptData.payment_method)}
                                </div>
                            </div>

                            <div>
                                <label className="block font-semibold mb-1 text-gray-800 dark:text-white">
                                    Payment Status
                                </label>
                                <div className="w-full border rounded-md p-2 bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 cursor-not-allowed">
                                    {getPaymentStatusBadge(receiptData.payment_received)}
                                </div>
                            </div>
                        </div>

                        {/* Right Column */}
                        <div className="space-y-4">
                            <Input
                                label="Previous Balance (£)"
                                type="number"
                                name="previous_balance"
                                value={receiptData.previous_balance}
                                readOnly
                                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                            />

                            <Input
                                label="New Balance (£)"
                                type="number"
                                name="balance_amount"
                                value={receiptData.balance_amount}
                                readOnly
                                className={`bg-gray-100 dark:bg-gray-600 cursor-not-allowed font-semibold ${parseFloat(receiptData.balance_amount) > 0 ? 'text-red-600' : 'text-green-600'
                                    }`}
                            />

                            {/* <Input
                                label="Reference Number"
                                type="text"
                                name="reference_number"
                                value={receiptData.fid || 'N/A'}
                                readOnly
                                className="bg-gray-100 dark:bg-gray-600 cursor-not-allowed"
                            /> */}

                            <div>
                                <label className="block font-semibold mb-1 text-gray-800 dark:text-white">
                                    Comments
                                </label>
                                <textarea
                                    name="comments"
                                    className="w-full border rounded-md p-2 bg-gray-100 dark:bg-gray-600 text-gray-800 dark:text-white border-gray-300 dark:border-gray-600 cursor-not-allowed"
                                    rows="4"
                                    value={receiptData.comments || 'No Comments'}
                                    readOnly
                                ></textarea>
                            </div>
                        </div>
                    </div>

                    {/* Balance Summary Section */}
                    <div className="mt-6 pt-6 border-t border-gray-300 dark:border-gray-600">
                        <h3 className="text-lg font-semibold mb-4 text-gray-800 dark:text-white">Payment Summary</h3>
                        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                            <div className="bg-blue-50 dark:bg-blue-900/20 p-4 rounded-lg">
                                <h4 className="text-blue-800 dark:text-blue-300 font-semibold text-sm">Previous Balance</h4>
                                <p className="text-2xl font-bold text-blue-600 dark:text-blue-400">
                                    £{receiptData.previous_balance}
                                </p>
                            </div>
                            <div className="bg-green-50 dark:bg-green-900/20 p-4 rounded-lg">
                                <h4 className="text-green-800 dark:text-green-300 font-semibold text-sm">Payment Received</h4>
                                <p className="text-2xl font-bold text-green-600 dark:text-green-400">
                                    £{receiptData.amount}
                                </p>
                            </div>
                            <div className={`p-4 rounded-lg ${parseFloat(receiptData.balance_amount) > 0
                                ? 'bg-red-50 dark:bg-red-900/20'
                                : 'bg-green-50 dark:bg-green-900/20'
                                }`}>
                                <h4 className={
                                    parseFloat(receiptData.balance_amount) > 0
                                        ? 'text-red-800 dark:text-red-300'
                                        : 'text-green-800 dark:text-green-300'
                                }>
                                    New Balance
                                </h4>
                                <p className={`text-2xl font-bold ${parseFloat(receiptData.balance_amount) > 0
                                    ? 'text-red-600 dark:text-red-400'
                                    : 'text-green-600 dark:text-green-400'
                                    }`}>
                                    £{receiptData.balance_amount}
                                </p>
                            </div>
                        </div>
                    </div>

                    <div className="flex justify-end mt-8">
                        <ReceiptPdfButton data={receiptData} />
                        <button
                            type="button"
                            onClick={() => setShowPdfPreview(!showPdfPreview)}
                            className="mx-4 bg-blue-600 hover:bg-blue-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                        >
                            {!showPdfPreview ? 'Show PDF Preview' : "Hide PDF Preview"}
                        </button>
                        <button
                            type="button"
                            onClick={() => navigate(-1)}
                            className="bg-gray-600 hover:bg-gray-700 text-white font-semibold px-6 py-2 rounded-md shadow text-sm sm:text-base"
                        >
                            Close
                        </button>
                    </div>
                </div>
            </div>
        </DashboardLayout>
    );
};

export default ReceiptView;